import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, catchError, of, map, tap, switchMap, from } from 'rxjs';
import { getConfig } from '../config.service';
import {
  IDefect,
  IDefectModel,
  IDefectPictureList,
  DefectLocationInfo,
  IGlobalLocation,
  IEmployee,
  SkomapLocation,
} from './defect.models';
import { IDefectFiltersDto } from './defect-overview/defect-overview.models';

/**
 * Building response from API.
 * Matches legacy IBuilding interface from web-app-legacy/js/app/Models/Contracts.ts
 */
interface IBuilding {
  id: number;
  campusId: number;
  code: string;
  name: string;
  description: string;
  managers: IEmployee[];
  coordinator: IEmployee;
}

/**
 * Detects the image MIME type from the magic bytes; falls back to the blob's
 * own type and finally to image/jpeg.
 */
function sniffImageType(bytes: Uint8Array, fallback: string): string {
  if (bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png';
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
  if (bytes.length >= 3 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return 'image/gif';
  if (bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d) return 'image/bmp';
  if (bytes.length >= 12 && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
    && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) return 'image/webp';
  return fallback.startsWith('image/') ? fallback : 'image/jpeg';
}

/**
 * Converts picture data to a data: URL with a proper image MIME type.
 */
async function blobToImageDataUrl(blob: Blob): Promise<string> {
  const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  const typed = new Blob([blob], { type: sniffImageType(head, blob.type) });
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(typed);
  });
}

/**
 * Service for defect CRUD operations.
 * Migrated from web-app-legacy/js/app/Components/Defects/DefectDataService.ts
 */
@Injectable({
  providedIn: 'root',
})
export class DefectService {
  private http = inject(HttpClient);
  private get baseUrl() { return getConfig().api.middlewareUrl; }

  /**
   * Create a new defect.
   * POST /api/defect/add
   */
  createDefect(model: IDefectModel): Observable<IDefectModel> {
    return this.http.post<IDefectModel>(`${this.baseUrl}/api/defect/add`, model).pipe(
      catchError((error) => {
        console.error('[DefectService] Error creating defect:', error);
        throw error;
      })
    );
  }

  /**
   * Update an existing defect.
   * POST /api/defect/edit
   */
  updateDefect(model: IDefectModel): Observable<IDefectModel> {
    return this.http.post<IDefectModel>(`${this.baseUrl}/api/defect/edit`, model).pipe(
      catchError((error) => {
        console.error('[DefectService] Error updating defect:', error);
        throw error;
      })
    );
  }

  /**
   * Get defect detail with POI.
   * GET /api/defect/GetWithPoi/{id}
   */
  getDefectWithPoi(defectId: number): Observable<IDefectModel> {
    return this.http.get<IDefectModel>(`${this.baseUrl}/api/defect/GetWithPoi/${defectId}`).pipe(
      catchError((error) => {
        console.error('[DefectService] Error getting defect:', error);
        throw error;
      })
    );
  }

  /**
   * Get the defect linked to a POI marker.
   * GET /api/defect/GetDefectByPoiId/{siteId}/{poiId}
   */
  getDefectByPoiId(siteId: number, poiId: number): Observable<IDefect> {
    return this.http
      .get<IDefect>(`${this.baseUrl}/api/defect/GetDefectByPoiId/${siteId}/${poiId}`)
      .pipe(
        catchError((error) => {
          console.error('[DefectService] Error getting defect by POI id:', error);
          throw error;
        })
      );
  }

  /**
   * Open defects reported for a room (FMS functional area), newest first.
   * GET /api/defect/GetDefectsByFunctionalArea/{siteId}/{functionalAreaId}
   */
  getDefectsByFunctionalArea(siteId: number, functionalAreaId: number): Observable<IDefect[]> {
    return this.http
      .get<IDefect[]>(`${this.baseUrl}/api/defect/GetDefectsByFunctionalArea/${siteId}/${functionalAreaId}`)
      .pipe(
        catchError((error) => {
          console.error('[DefectService] Error getting defects by functional area:', error);
          throw error;
        })
      );
  }

  /**
   * Upload images for a defect.
   * POST /api/defect/UploadPictureFromFormData/{id}
   */
  uploadImages(defectId: number, files: File[]): Observable<any> {
    if (files.length === 0) {
      return of(null);
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('file', file);
    });

    return this.http.post(`${this.baseUrl}/api/defect/UploadPictureFromFormData/${defectId}`, formData).pipe(
      catchError((error) => {
        console.error('[DefectService] Error uploading images:', error);
        throw error;
      })
    );
  }

  /**
   * Get defect images.
   * GET /api/defect/DefectPictures/{id}
   */
  getDefectImages(defectId: number): Observable<IDefectPictureList> {
    return this.http.get<IDefectPictureList>(`${this.baseUrl}/api/defect/DefectPictures/${defectId}`).pipe(
      catchError((error) => {
        console.error('[DefectService] Error getting defect images:', error);
        return of({ items: [] });
      })
    );
  }

  /**
   * Delete a defect image.
   * DELETE /api/defect/DeletePicture/{imageId}
   */
  deleteDefectImage(imageId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/defect/DeletePicture/${imageId}`).pipe(
      catchError((error) => {
        console.error('[DefectService] Error deleting image:', error);
        throw error;
      })
    );
  }

  /**
   * Loads the picture data through HttpClient (so the Bearer token is sent; a
   * plain <img src> pointing to the API gets 401) and returns a data: URL
   * usable as <img src>. A data: URL is used instead of a blob: object URL
   * because the deployed Content-Security-Policy allows "img-src * data:" but
   * not blob:. Pictures uploaded by the legacy app are stored without a
   * content type, so the image type is sniffed from the data.
   * GET /api/defect/DefectPictureData/{id}
   */
  getImageDataUrl(imageId: string): Observable<string> {
    return this.http
      .get(`${this.baseUrl}/api/defect/DefectPictureData/${imageId}`, { responseType: 'blob' })
      .pipe(switchMap((blob) => from(blobToImageDataUrl(blob))));
  }

  /**
   * Locate building by coordinates.
   * POST /api/pois/GetBuildingByLocNull/{siteId}
   * Migrated from web-app-legacy/js/app/services/FmsService.ts - getBuildingByLocation
   */
  locateBuildingByCoordinates(
    siteId: number,
    _navvisSiteId: number,
    location: IGlobalLocation
  ): Observable<DefectLocationInfo | null> {
    // Convert global location to SkomapLocation format
    // Note: lat=y, lon=x (geographic convention)
    const skomapLocation: SkomapLocation = {
      lat: location.y,
      lon: location.x,
      z: location.z,
    };

    return this.http
      .post<IBuilding | null>(`${this.baseUrl}/api/pois/GetBuildingByLocNull/${siteId}`, skomapLocation)
      .pipe(
        map((building) => {
          if (!building) {
            return null;
          }

          // IBuilding already includes managers and coordinator
          return {
            id: building.id,
            name: building.name || building.code || `Building ${building.id}`,
            managers: building.managers || [],
            coordinator: building.coordinator,
            isOutdoorLocation: false,
          };
        }),
        catchError((error) => {
          console.error('[DefectService] Error locating building:', error);
          // Return null to trigger unscanned building dialog
          return of(null);
        })
      );
  }

  /**
   * Get list of unscanned (non-scanned) buildings for a site.
   * Used when defect location is outside scanned areas to let user pick a building.
   * GET /api/fms/nonScannedBuildings/{siteId}
   */
  getUnscannedBuildings(siteId: number): Observable<IBuilding[]> {
    return this.http.get<IBuilding[]>(`${this.baseUrl}/api/fms/nonScannedBuildings/${siteId}`).pipe(
      catchError((error) => {
        console.error('[DefectService] Error getting unscanned buildings:', error);
        return of([]);
      })
    );
  }

  /**
   * Simple building lookup by FMS building ID.
   * GET /api/fms/building/{buildingId}
   */
  getBuildingInfo(buildingId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/api/fms/building/${buildingId}`).pipe(
      catchError((error) => {
        console.error('[DefectService] Error getting building info:', error);
        return of(null);
      })
    );
  }

  /**
   * Filter defects with the given filters.
   * POST /api/defect/filter/{siteId}
   */
  filterDefects(siteId: number, filters: IDefectFiltersDto): Observable<IDefect[]> {
    return this.http.post<IDefect[]>(`${this.baseUrl}/api/defect/filter/${siteId}`, filters).pipe(
      catchError((error) => {
        console.error('[DefectService] Error filtering defects:', error);
        return of([]);
      })
    );
  }

  /**
   * Export defects to Excel file.
   * POST /api/defect/export/{siteId}
   */
  exportDefects(siteId: number, filters: IDefectFiltersDto): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .post(`${this.baseUrl}/api/defect/export/${siteId}`, filters, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          const filename = this.getFilenameFromHeaders(response.headers) || 'defects.xlsx';
          return { blob: response.body!, filename };
        }),
        catchError((error) => {
          console.error('[DefectService] Error exporting defects:', error);
          throw error;
        })
      );
  }

  /**
   * Export defect detail to PDF.
   * GET /api/export/defectpdf/{defectId}
   */
  exportDefectPdf(defectId: number): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(`${this.baseUrl}/api/export/defectpdf/${defectId}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          const filename = this.getFilenameFromHeaders(response.headers) || `defect-${defectId}.pdf`;
          return { blob: response.body!, filename };
        }),
        catchError((error) => {
          console.error('[DefectService] Error exporting defect PDF:', error);
          throw error;
        })
      );
  }

  /**
   * Report defect to SAP.
   * POST /api/defect/report-to-sap/{defectId}
   */
  reportToSap(defectId: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/api/defect/report-to-sap/${defectId}`, null).pipe(
      catchError((error) => {
        console.error('[DefectService] Error reporting to SAP:', error);
        throw error;
      })
    );
  }

  /**
   * Helper to trigger file download.
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Extract filename from Content-Disposition header.
   */
  private getFilenameFromHeaders(headers: any): string | null {
    const contentDisposition = headers.get('content-disposition');
    if (!contentDisposition) {
      return null;
    }

    // Pattern to match filename from Content-Disposition header
    const pattern = /filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/gi;
    let match: RegExpExecArray | null;

    // Execute pattern twice to get the actual filename (second match group)
    pattern.exec(contentDisposition);
    match = pattern.exec(contentDisposition);

    if (match && match.length >= 4 && match[3]) {
      return decodeURIComponent(match[3]);
    }

    return null;
  }
}
